import React, { useState, useEffect, useMemo, useContext } from 'react'
import { Modal, Select, Input, InputNumber, DatePicker, Icon, Tooltip, message } from 'antd'
import moment from 'moment'
import http from '../../util/Api'
import PhotoLightbox from './PhotoLightbox'
import InlineCreateMasterModal from '../../components/InlineCreateMasterModal'
import ClassificationSearch, { rowFromMasters, findRowForModel } from './ClassificationSearch'

/**
 * NewAssetDetails
 *
 * Everything the PV Approval form can set on an asset, brought into the cockpit
 * so a PV item processed as NEW_ASSET arrives in the register classified and
 * priced instead of as "Unclassified Asset Type" with no value.
 *
 * Two things make it usable at onboarding scale. Measured on AAV-1 at
 * pinto-rosario-square: 269 PV items, of which 135 are foot lights, 32 junction
 * boxes and 28 spotlights — commodity items differing in nothing a form can
 * capture, and 246 carrying no sub-location at all.
 *
 *  1. **Fill by group.** Pick a dimension — model, sub-type, type, location or
 *     sub-location — and the selection collapses into groups filled once each.
 *     Model produces seven groups covering all 269 records.
 *
 *  2. **Override by asset.** Opening one record unlocks the fields that identify
 *     a single object, and marks them so a later group apply cannot silently
 *     overwrite them.
 *
 * ── What may be filled for a group, and what may not ──────────────────────────
 *
 * A field describing a KIND of thing is safe to apply to many records. A field
 * identifying ONE thing is not: copying a serial number across 135 assets writes
 * 135 records claiming to be the same object. Those fields are shown, disabled,
 * in group mode rather than hidden, so it is clear they exist and clear why they
 * are not on offer here.
 *
 * ── Nothing is filled in silently ────────────────────────────────────────────
 *
 * Every field records where its value came from — the scan, a group apply, or an
 * individual edit — and an unset field stays unset. Values are never defaulted,
 * carried between groups, or inferred, and a required field left empty blocks
 * creation rather than being quietly replaced by 0 or a placeholder.
 */

const { Option } = Select
const { TextArea } = Input

// Field names match the PV Approval form's payload so the server can map them
// straight across; see docs/BACKEND-REQUEST-reconciliation.md item 7.
export const DETAIL_FIELDS = [
  'assetType', 'equipmentSubType', 'assetModel',
  'ownedBy', 'rate', 'invoiceDate',
  'physicalCondition', 'utilisationLevel', 'assetCustodian',
  'altName', 'description', 'countable', 'qty',
  'serialNumber', 'existingAssetCode', 'parentAssetCode'
]

// Set for one record only. Applying these to a group would write the same
// identity onto many distinct objects.
export const PER_ASSET_ONLY = ['serialNumber', 'existingAssetCode', 'parentAssetCode']

// Without these the asset cannot be created. Deliberately short: the PV Approval
// bulk form requires exactly owner and invoice value, and adding more would block
// work on fields nobody has agreed are mandatory.
export const REQUIRED = ['ownedBy', 'rate']

export const GROUP_DIMS = [
  { key: 'assetModel', label: 'Asset model' },
  { key: 'equipmentSubType', label: 'Equipment sub-type' },
  { key: 'assetType', label: 'Asset type' },
  { key: 'location', label: 'Location' },
  { key: 'client', label: 'Sub-location' }
]

const idOf = (v) => (v && typeof v === 'object' ? v._id : v) || null
const nameOf = (v) => (v && typeof v === 'object' ? v.name : null)

/**
 * The value a record starts with, before anyone has filled anything in.
 *
 * A PV item carries its own equipment. An AST record does not - its classification
 * lives on the asset its scanned code points at (`matchedAsset`), which is exactly
 * what a duplicate turned into a new asset should start from. Without this the
 * form opened blank for AST duplicates and the operator retyped what the scan
 * already knew.
 */
// Object-reference fields the scan carries (stored as an id / populated {_id,name}).
const SCAN_REF = ['assetType', 'equipmentSubType', 'assetModel', 'physicalCondition', 'assetCustodian']

// Scalar fields the scan carries directly (not object refs): how many units were
// counted (qty), whether the model is countable, and the rest of the values the
// auditor typed on the scan form.
const SCAN_SCALAR = ['qty', 'countable', 'utilisationLevel', 'serialNumber', 'existingAssetCode', 'parentAssetCode']

// Some detail fields are named differently on the source record: a PV stores the
// condition under `physicalCondition`, an AST under `condition`. Custodian is only
// on a PV; anything not present simply stays unset.
const REC_ALIASES = { physicalCondition: ['physicalCondition', 'condition'] }
const recKeys = (field) => REC_ALIASES[field] || [field]

const hasVal = (v) => v !== undefined && v !== null && v !== ''

export const scanValue = (rec, field) => {
  const keys = recKeys(field)
  if (SCAN_SCALAR.includes(field)) {
    for (let i = 0; i < keys.length; i++) {
      if (rec && hasVal(rec[keys[i]])) return rec[keys[i]]
    }
    const m = rec && rec.matchedAsset
    if (m) for (let i = 0; i < keys.length; i++) {
      if (hasVal(m[keys[i]])) return m[keys[i]]
    }
    return undefined
  }
  if (!SCAN_REF.includes(field)) return undefined
  for (let i = 0; i < keys.length; i++) {
    const own = idOf(rec[keys[i]])
    if (own) return own
  }
  const m = rec && rec.matchedAsset
  if (m) for (let i = 0; i < keys.length; i++) {
    const own = idOf(m[keys[i]])
    if (own) return own
  }
  return undefined
}

/** Resolved value for a record's field: an edit if there is one, else the scan. */
export const valueOf = (details, rec, field) => {
  const d = details[rec._uid]
  if (d && d.fields && Object.prototype.hasOwnProperty.call(d.fields, field)) return d.fields[field]
  return scanValue(rec, field)
}

export const provOf = (details, rec, field) => {
  const d = details[rec._uid]
  if (d && d.prov && d.prov[field]) return d.prov[field]
  return scanValue(rec, field) != null ? 'scan' : null
}

/** Which required fields a record is still missing. */
export const missingFor = (details, rec) =>
  REQUIRED.filter(f => {
    const v = valueOf(details, rec, f)
    return v === undefined || v === null || v === ''
  })

export const isReady = (details, rec) => missingFor(details, rec).length === 0

const REQ_LABEL = { ownedBy: 'owner', rate: 'invoice value' }

// The owner is the same for asset after asset in a single onboarding run, so the
// last one picked is remembered and offered again. It is a convenience default,
// not a guess about the data: it is applied visibly, labelled as a default in the
// field's provenance line, and any field can be changed before Apply.
const LAST_OWNER_KEY = 'rcx.lastNewAssetOwner'
export const readLastOwner = () => {
  try { return localStorage.getItem(LAST_OWNER_KEY) || null } catch (e) { return null }
}
const writeLastOwner = (id) => {
  try { if (id) localStorage.setItem(LAST_OWNER_KEY, id) } catch (e) { /* private mode */ }
}

// Field is defined at module scope (not inside NewAssetDetails) so its component
// TYPE is stable across renders. Defined inline, every keystroke re-created it and
// React remounted the subtree, which stole focus from the input after one key. The
// dynamic bits (currentOf / provSummary / single) come through context, so the type
// stays constant while the values update.
const FieldCtx = React.createContext(null)

const Field = ({ field, label, children, hint, wide }) => {
  const { currentOf, provSummary, single } = useContext(FieldCtx)
  const locked = !single && PER_ASSET_ONLY.includes(field)
  const cur = currentOf(field)
  const prov = provSummary(field)
  const req = REQUIRED.includes(field)
  const empty = cur.v === undefined || cur.v === null || cur.v === ''
  return (
    <div className={'nad-f' + (wide ? ' wide' : '') + (locked ? ' locked' : '') + (req && empty && !cur.mixed ? ' req' : '')}>
      <label>
        {label}
        {req && <em className="nad-req">required</em>}
      </label>
      {locked
        ? <div className="nad-lock"><Icon type="lock" /> Open a single asset to set</div>
        : children({ value: cur.v, mixed: cur.mixed })}
      <span className={'nad-prov ' + (prov === 'individual' ? 'ind' : prov === 'scan' ? 'scan' : prov && prov.startsWith('group') ? 'grp' : '')}>
        {cur.dirty ? 'not applied yet'
          : cur.mixed ? 'mixed across these assets'
            : prov === 'scan' ? 'from the scan'
              : prov === 'individual' ? 'set on this asset'
                : prov === 'default' ? (field === 'rate' ? 'default 0 — change if the asset has a value' : 'default — last used')
                  : prov === 'mixed' ? 'mixed sources'
                    : prov && prov.startsWith('group:') ? `set for ${prov.slice(6)}`
                      : hint || 'not set'}
      </span>
    </div>
  )
}

// Master lists kept across opens. The form is mounted fresh every time it opens
// (index.js renders it only while detailsFor is set), so component state alone
// re-downloaded all five lists on every click. Reference data (types, sub-types,
// models, conditions, custodians) - kept for 15 minutes, never when a list failed
// to load, and updated in place when a master is created from this form. The
// classification search itself always queries the server live.
// The cache belongs to one organisation and user (cacheKey): signing out does not
// reload the page, so without the key the next organisation signed in within 15
// minutes would be offered the previous one's lists and ids. No key = no cache.
const MASTERS_TTL_MS = 15 * 60 * 1000
let mastersCache = null // { at, key, data }
const cachedMasters = (key) => (key && mastersCache && mastersCache.key === key &&
  Date.now() - mastersCache.at < MASTERS_TTL_MS ? mastersCache.data : null)

const NewAssetDetails = ({ visible, records, details, onApply, onClose, ownerOptions = [], cacheKey = null }) => {
  const [dim, setDim] = useState('assetModel')
  const [groupKey, setGroupKey] = useState(null)
  const [draft, setDraft] = useState({})
  const [masters, setMastersState] = useState(() => cachedMasters(cacheKey))
  const setMasters = (next) => setMastersState(prev => {
    const val = typeof next === 'function' ? next(prev) : next
    const complete = val && ['assetType', 'equipmentSubType', 'assetModel', 'physicalCondition', 'custodian'].every(k => Array.isArray(val[k]))
    if (complete && cacheKey) {
      const sameEntry = mastersCache && mastersCache.key === cacheKey && mastersCache.data === prev
      mastersCache = { at: sameEntry ? mastersCache.at : Date.now(), key: cacheKey, data: val }
    }
    return val
  })
  const [loadingMasters, setLoadingMasters] = useState(false)
  // Opens on the two fields that block creation. Everything else is real but
  // rarely needed on an onboarding run, so it waits behind one click.
  const [expanded, setExpanded] = useState(false)
  const [lightbox, setLightbox] = useState(-1)
  const [pickedRows, setPickedRows] = useState({}) // model id -> picked { model, subType, type }

  const single = records && records.length === 1

  // The audit photo is the only place a serial plate or a model number actually
  // exists at this point - the operator is reading details off it to type them in.
  // Shown for a single asset, where "this photo" is unambiguous.
  const photos = single && records[0] && Array.isArray(records[0].photos)
    ? records[0].photos.filter(p => p && p.url)
    : []
  const photoCode = single && records[0]
    ? (records[0].tempAssetCode || records[0].assetCode || '')
    : ''

  // Master data: from the module cache when fresh (see mastersCache), else fetched
  // once here - five lists in parallel.
  useEffect(() => {
    if (!visible || masters || loadingMasters) return
    setLoadingMasters(true)
    const grab = (p) => http.get(p)
      .then(r => {
        const d = r.data && r.data.data ? r.data.data : r.data
        const arr = Array.isArray(d) ? d : (d && d.data) || []
        return Array.isArray(arr) ? arr : []
      })
      .catch(() => null)   // null = "could not load", distinct from [] = "none exist"
    Promise.all([
      grab('asset-type'), grab('equipment-sub-type'), grab('asset-model'), grab('condition-of-asset'), grab('manpower')
    ]).then(([assetType, equipmentSubType, assetModel, physicalCondition, custodian]) => {
      setMasters({ assetType, equipmentSubType, assetModel, physicalCondition, custodian })
      setLoadingMasters(false)
    })
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  // Groups over the current selection, by the chosen dimension.
  const groups = useMemo(() => {
    if (!records || !records.length) return []
    const by = new Map()
    records.forEach(r => {
      const raw = r[dim]
      const key = idOf(raw) || '__none__'
      const label = nameOf(raw) || (dim === 'client' ? 'No sub-location' : 'Not set')
      if (!by.has(key)) by.set(key, { key, label, recs: [] })
      by.get(key).recs.push(r)
    })
    return [...by.values()]
      .map(g => {
        const ready = g.recs.filter(r => isReady(details, r)).length
        return { ...g, ready, pct: g.recs.length ? Math.round((ready / g.recs.length) * 100) : 0 }
      })
      .sort((a, b) => b.recs.length - a.recs.length)
  }, [records, dim, details])

  useEffect(() => {
    if (!groups.length) { setGroupKey(null); return }
    if (single) { setGroupKey(groups[0].key); return }
    if (!groups.some(g => g.key === groupKey)) setGroupKey(groups[0].key)
  }, [groups, single]) // eslint-disable-line react-hooks/exhaustive-deps

  // A new target clears the draft: a value typed for foot lights must never
  // survive into junction boxes just because the form stayed open.
  useEffect(() => { setDraft({}) }, [groupKey, dim, visible])
  useEffect(() => { if (!visible) setExpanded(false) }, [visible])

  // Seed the two required fields the moment the form opens, so it opens ready to
  // create rather than opening red. Owner is the last one used (else the first on
  // the list) and invoice value starts at 0.
  //
  // These are applied to the records, not just parked in the draft, so the
  // readout and the Details column agree with what is on screen - and they are
  // stamped 'default', which the provenance line prints, so nobody mistakes a
  // seeded 0 for a value somebody entered. Only records that have NOTHING set are
  // touched; an asset already given an owner or a price is never overwritten.
  useEffect(() => {
    if (!visible || !records || !records.length) return
    const owner = readLastOwner()
      || (ownerOptions[0] && ownerOptions[0]._id)
      || null
    const blank = records.filter(r => missingFor(details, r).length > 0)
    if (!blank.length) return
    const seed = {}
    if (owner && blank.some(r => missingFor(details, r).includes('ownedBy'))) seed.ownedBy = owner
    if (blank.some(r => missingFor(details, r).includes('rate'))) seed.rate = 0
    if (Object.keys(seed).length) onApply(blank, seed, 'default')
  }, [visible, records]) // eslint-disable-line react-hooks/exhaustive-deps

  const active = groups.find(g => g.key === groupKey) || null
  const targets = active ? active.recs : []

  // What the target already shows: one agreed value, or a mix.
  const currentOf = (field) => {
    if (Object.prototype.hasOwnProperty.call(draft, field)) return { v: draft[field], mixed: false, dirty: true }
    if (!targets.length) return { v: undefined, mixed: false }
    const vals = new Set(targets.map(r => {
      const v = valueOf(details, r, field)
      return v === undefined || v === '' ? null : v
    }))
    if (vals.size > 1) return { v: undefined, mixed: true }
    const only = [...vals][0]
    return { v: only === null ? undefined : only, mixed: false }
  }

  const provSummary = (field) => {
    if (!targets.length) return null
    const provs = new Set(targets.map(r => provOf(details, r, field)))
    if (provs.size > 1) return 'mixed'
    return [...provs][0]
  }

  const set = (field, v) => setDraft(d => ({ ...d, [field]: v }))

  const overwriteCount = useMemo(() => {
    if (single) return 0
    const touched = Object.keys(draft)
    if (!touched.length) return 0
    return targets.filter(r => touched.some(f => provOf(details, r, f) === 'individual')).length
  }, [draft, targets, details, single])

  // Everything the targets need is already set - by the seeded defaults or by an
  // earlier apply. The primary button is then a confirmation, not a no-op, so it
  // stays enabled and closes.
  const allReady = targets.length > 0 && targets.every(r => isReady(details, r))

  const apply = () => {
    const touched = Object.keys(draft)
    if (!touched.length) {
      if (allReady) return onClose()
      return message.info('Nothing to apply — no field was changed.')
    }
    const label = single ? 'individual' : `group:${active ? active.label : ''}`
    const run = () => {
      if (draft.ownedBy) writeLastOwner(draft.ownedBy)
      onApply(targets, draft, label)
      message.success(
        single
          ? `Details set on ${targets[0] && (targets[0].tempAssetCode || targets[0].assetCode)}.`
          : `${touched.length} field${touched.length === 1 ? '' : 's'} applied to ${targets.length} asset${targets.length === 1 ? '' : 's'}.`
      )
      setDraft({})
    }
    if (overwriteCount > 0) {
      Modal.confirm({
        title: `Overwrite ${overwriteCount} individual edit${overwriteCount === 1 ? '' : 's'}?`,
        content: `${overwriteCount} of these ${targets.length} assets had ${overwriteCount === 1 ? 'this field' : 'these fields'} set individually. Applying the group value replaces ${overwriteCount === 1 ? 'it' : 'them'}.`,
        okText: 'Replace them',
        okType: 'danger',
        onOk: run
      })
    } else run()
  }

  // Readout across the whole selection, not just the open group.
  const totals = useMemo(() => {
    if (!records) return { ready: 0, total: 0, missing: {} }
    const missing = {}
    let ready = 0
    records.forEach(r => {
      const m = missingFor(details, r)
      if (!m.length) ready++
      m.forEach(f => { missing[f] = (missing[f] || 0) + 1 })
    })
    return { ready, total: records.length, missing }
  }, [records, details])

  const taxonomyThin = masters
    && (masters.assetType || []).length <= 1
    && (masters.equipmentSubType || []).length <= 1

  const opts = (list) => (list || []).map(o => (
    <Option key={o._id} value={o._id}>{o.name}</Option>
  ))

  // Classification is picked as one row (model + sub-type + type) from
  // ClassificationSearch; these are read for the "set without a model" readout
  // and to seed the inline model create with the current sub-type.
  const curType = currentOf('assetType').v
  const curSub = currentOf('equipmentSubType').v

  // Countability is decided by the chosen model, not the auditor: it drives the
  // (disabled) Countable field, and quantity is only offered when it is countable.
  const curModel = currentOf('assetModel').v
  const selectedModelObj = ((masters && masters.assetModel) || []).find(m => `${m._id}` === `${curModel}`)
  const modelCountable = selectedModelObj ? !!selectedModelObj.isCountable : undefined
  const effCountable = (modelCountable !== undefined) ? modelCountable : !!currentOf('countable').v

  // Rows picked from the classification search, by model id, so the pick shows
  // its names even when that model is not in the loaded master lists.
  const pickClassification = (row) => {
    setPickedRows(prev => ({ ...prev, [String(row.model._id)]: row }))
    // Model, sub-type and type are set together - they can never disagree.
    set('assetModel', row.model._id)
    set('equipmentSubType', row.subType._id)
    set('assetType', row.type._id)
    const cnt = !!row.model.isCountable
    set('countable', cnt)
    if (!cnt) set('qty', 1)
  }
  const clearClassification = () => { set('assetModel', null); set('equipmentSubType', null); set('assetType', null) }
  const nameIn = (list, id) => { const o = (list || []).find(x => String(x._id) === String(id)); return o ? o.name : null }

  // A model created inline is appended to its list and selected, with its
  // sub-type and type, so the three stay consistent.
  const addMaster = (listKey, item) => setMasters(prev => ({ ...(prev || {}), [listKey]: [...((prev && prev[listKey]) || []), item] }))
  // A model created inline is selected straight away, with its sub-type and type,
  // exactly as if it had been picked from the search. The names come from what the
  // create returned (its sub-type object) or the loaded lists; if a part is still
  // unknown - e.g. its sub-type or type was itself just created in that dialog -
  // the row is read once from the search endpoint, matched on the new model's id.
  const rowFromCreated = (c) => {
    const subId = idOf(c.assetSubType)
    const subObj = (c.assetSubType && typeof c.assetSubType === 'object') ? c.assetSubType
      : ((masters && masters.equipmentSubType) || []).find(x => String(x._id) === String(subId))
    const subName = (subObj && subObj.name) || nameIn(masters && masters.equipmentSubType, subId)
    const typeRaw = subObj && subObj.assetType
    const typeId = idOf(typeRaw)
    const typeName = (typeRaw && typeof typeRaw === 'object' && typeRaw.name) || nameIn(masters && masters.assetType, typeId)
    if (!(c._id && c.name && subId && subName && typeId && typeName)) return null
    return {
      model: { _id: c._id, name: c.name, isCountable: !!c.isCountable },
      subType: { _id: subId, name: subName },
      type: { _id: typeId, name: typeName }
    }
  }
  const onModelCreated = (c) => {
    if (!c || !c._id) return
    addMaster('assetModel', c)
    const row = rowFromCreated(c)
    if (row) return pickClassification(row)
    // Set what is known now so nothing is lost, then complete the row.
    set('assetModel', c._id)
    const subId = idOf(c.assetSubType)
    if (subId) set('equipmentSubType', subId)
    const cnt = !!c.isCountable
    set('countable', cnt)
    if (!cnt) set('qty', 1)
    findRowForModel(c._id, c.name)
      .then(found => {
        if (found) pickClassification(found)
        else message.warning(`${c.name || 'The new model'} was created, but its sub-type and type could not be read — search for it to select it.`)
      })
      .catch(() => message.warning(`${c.name || 'The new model'} was created, but its sub-type and type could not be read — search for it to select it.`))
  }


  const title = single && records && records[0]
    ? `Asset details — ${records[0].tempAssetCode || records[0].assetCode}`
    : `Asset details — ${(records || []).length} new asset${(records || []).length === 1 ? '' : 's'}`

  return (
    <FieldCtx.Provider value={{ currentOf, provSummary, single }}>
    <Modal
      visible={visible} onCancel={onClose} footer={null} width={single ? 780 : 1080}
      className="rcx-nad" title={title} destroyOnClose maskClosable={false}
    >
      <div className={'nad' + (single ? ' solo' : '')}>
        {!single && (
          <div className="nad-rail">
            <div className="nad-railh">Group by</div>
            <Select size="small" value={dim} onChange={setDim} style={{ width: '100%', marginBottom: 10 }}>
              {GROUP_DIMS.map(d => <Option key={d.key} value={d.key}>{d.label}</Option>)}
            </Select>
            <div className="nad-groups">
              {groups.map(g => (
                <button key={g.key} type="button"
                  className={'nad-grp' + (g.key === groupKey ? ' on' : '')}
                  onClick={() => setGroupKey(g.key)}>
                  <span className={'nad-meter ' + (g.pct === 100 ? 'full' : g.pct === 0 ? 'none' : 'part')}>
                    <i style={{ width: `${g.pct}%` }} />
                  </span>
                  <span className="nad-gn">
                    <b>{g.label}</b>
                    <span>{g.ready} of {g.recs.length} ready</span>
                  </span>
                  <span className="nad-cnt tnum">{g.recs.length}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="nad-main">
          {!single && active && (
            <div className="nad-head">
              <div>
                <h4>{active.label}</h4>
                <p>{targets.length} asset{targets.length === 1 ? '' : 's'} · {active.ready} ready to create</p>
              </div>
              <span className="nad-scope">Applies to {targets.length}</span>
            </div>
          )}

          {taxonomyThin && (
            <div className="nad-warn">
              <Icon type="exclamation-circle" theme="filled" />
              <span>
                This organisation has no classification to assign — {(masters.assetType || []).length} asset
                type and {(masters.equipmentSubType || []).length} sub-type exist. Build the taxonomy in{' '}
                <b>Asset Reclassification</b> first; assets created now will stay unclassified.
              </span>
            </div>
          )}

          {masters === null
            ? <div className="nad-loading"><Icon type="loading" /> Loading classification lists…</div>
            : (
              <>
                {photos.length > 0 && (
                  <div className="nad-photos">
                    <div className="nad-photos-h">
                      <span>Audit photo{photos.length > 1 ? `s (${photos.length})` : ''}</span>
                      <em>Click to zoom — read the serial or model plate off the picture</em>
                    </div>
                    <div className="nad-photos-strip">
                      {photos.map((p, i) => (
                        <button type="button" key={p.url || i} className="nad-photo"
                          onClick={() => setLightbox(i)} title="Open and zoom">
                          <img src={p.url} alt={`${photoCode} audit photo ${i + 1}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <section className="nad-set">
                  <h5>Classification</h5>
                  <div className="nad-grid">
                    {/* One search across model, sub-type and type, as in the PV form: a
                        pick sets all three together. See ClassificationSearch.js. */}
                    <Field field="assetModel" label="Asset model · sub-type · type" wide>
                      {({ value, mixed }) => (
                        <div>
                          <div className="nad-withnew">
                            <ClassificationSearch value={value} mixed={mixed}
                              selected={(value && (pickedRows[String(value)] || rowFromMasters(masters, value))) || null}
                              onPick={pickClassification} onClear={clearClassification} />
                            <InlineCreateMasterModal item="AssetModel" onCreated={onModelCreated} initialAssetSubTypeId={curSub} triggerStyle={{ margin: 0 }} />
                          </div>
                          {/* A sub-type / type set without a model (e.g. from the scan) is shown, not hidden. */}
                          {!value && !mixed && (curSub || curType) && (
                            <div className="nad-partial">
                              Set without a model: {[nameIn(masters.equipmentSubType, curSub), nameIn(masters.assetType, curType)].filter(Boolean).join(' · ') || 'ids not in the loaded lists'}
                            </div>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                </section>

                <section className="nad-set">
                  <h5>Commercial</h5>
                  <div className="nad-grid">
                    <Field field="ownedBy" label="Owned by">
                      {({ value, mixed }) => (
                        <Select allowClear showSearch optionFilterProp="children" size="small"
                          value={value} placeholder={mixed ? 'Mixed — pick to set all' : 'Not set'}
                          onChange={(v) => set('ownedBy', v === undefined ? null : v)}>
                          {opts(ownerOptions)}
                        </Select>
                      )}
                    </Field>
                    <Field field="rate" label="Invoice value" hint="per asset, not a group total">
                      {({ value, mixed }) => (
                        <InputNumber size="small" min={0} value={value} style={{ width: '100%' }}
                          placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(v) => set('rate', v === undefined || v === null ? null : v)} />
                      )}
                    </Field>
                    {expanded && (
                    <Field field="invoiceDate" label="Invoice date">
                      {({ value, mixed }) => (
                        <DatePicker size="small" style={{ width: '100%' }} format="DD-MM-YYYY"
                          value={value ? moment(value) : null}
                          placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(d) => set('invoiceDate', d ? d.toISOString() : null)} />
                      )}
                    </Field>
                    )}
                  </div>
                </section>

                {!expanded && (
                  <button type="button" className="nad-expand" onClick={() => setExpanded(true)}>
                    <Icon type="down" /> More details
                    <em>condition, description, serial number, invoice date</em>
                  </button>
                )}

                {expanded && (
                <section className="nad-set">
                  <h5>Condition &amp; description</h5>
                  <div className="nad-grid">
                    <Field field="physicalCondition" label="Physical condition">
                      {({ value, mixed }) => (
                        <Select allowClear showSearch optionFilterProp="children" size="small"
                          value={value} placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(v) => set('physicalCondition', v === undefined ? null : v)}>
                          {opts(masters.physicalCondition)}
                        </Select>
                      )}
                    </Field>
                    <Field field="utilisationLevel" label="Utilisation level">
                      {({ value, mixed }) => (
                        <Select allowClear size="small" value={value}
                          placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(v) => set('utilisationLevel', v === undefined ? null : v)}>
                          {['Utilised', 'Partially Utilised', 'Non Utilised'].map(u => <Option key={u} value={u}>{u}</Option>)}
                        </Select>
                      )}
                    </Field>
                    <Field field="assetCustodian" label="Custodian">
                      {({ value, mixed }) => (
                        <Select allowClear showSearch optionFilterProp="children" size="small"
                          value={value} placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(v) => set('assetCustodian', v === undefined ? null : v)}>
                          {opts(masters.custodian)}
                        </Select>
                      )}
                    </Field>
                    <Field field="altName" label="Alt name">
                      {({ value, mixed }) => (
                        <Input size="small" value={value || ''} placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(e) => set('altName', e.target.value || null)} />
                      )}
                    </Field>
                    <Field field="countable" label="Countable" hint="set by the asset model">
                      {() => (
                        <Select size="small" disabled
                          value={curModel ? effCountable : undefined}
                          placeholder={curModel ? '' : 'Pick a model'}>
                          <Option value={true}>Yes</Option>
                          <Option value={false}>No</Option>
                        </Select>
                      )}
                    </Field>
                    <Field field="qty" label="Quantity" hint={effCountable ? 'per line — creates this many assets' : 'only for countable models'}>
                      {({ value, mixed }) => (
                        <InputNumber size="small" min={1} disabled={!effCountable}
                          value={effCountable ? value : 1} style={{ width: '100%' }}
                          placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(v) => set('qty', v === undefined || v === null ? null : v)} />
                      )}
                    </Field>
                    <div className="nad-f wide">
                      <label>Description</label>
                      <TextArea rows={2} value={currentOf('description').v || ''}
                        placeholder={currentOf('description').mixed ? 'Mixed across these assets' : 'Not set'}
                        onChange={(e) => set('description', e.target.value || null)} />
                    </div>
                  </div>
                </section>
                )}

                {expanded && (
                <section className="nad-set">
                  <h5>
                    Per-asset identity
                    {!single && <em> — cannot be filled for a group</em>}
                  </h5>
                  <div className="nad-grid">
                    <Field field="serialNumber" label="Serial number">
                      {({ value, mixed }) => (
                        <Input size="small" value={value || ''} placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(e) => set('serialNumber', e.target.value || null)} />
                      )}
                    </Field>
                    <Field field="existingAssetCode" label="Existing asset code">
                      {({ value, mixed }) => (
                        <Input size="small" value={value || ''} placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(e) => set('existingAssetCode', e.target.value || null)} />
                      )}
                    </Field>
                    <Field field="parentAssetCode" label="Parent asset code">
                      {({ value, mixed }) => (
                        <Input size="small" value={value || ''} placeholder={mixed ? 'Mixed' : 'Not set'}
                          onChange={(e) => set('parentAssetCode', e.target.value || null)} />
                      )}
                    </Field>
                  </div>
                </section>
                )}

                {expanded && (
                  <button type="button" className="nad-expand" onClick={() => setExpanded(false)}>
                    <Icon type="up" /> Fewer details
                  </button>
                )}
              </>
            )}
        </div>
      </div>

      <div className="nad-foot">
        <span className="nad-readout">
          <b className="tnum">{totals.ready} of {totals.total}</b> ready to create
          {Object.keys(totals.missing).length > 0 && (
            <em>
              {' · '}
              {REQUIRED.filter(f => totals.missing[f])
                .map(f => `${totals.missing[f]} need ${REQ_LABEL[f] || f}`)
                .join(' · ')}
            </em>
          )}
        </span>
        <span className="nad-btns">
          <button className="nad-b" onClick={onClose}>Done</button>
          <button className="nad-b pri" onClick={apply}
            disabled={!Object.keys(draft).length && !allReady}>
            {single ? 'Set on this asset' : `Apply to ${targets.length} asset${targets.length === 1 ? '' : 's'}`}
          </button>
        </span>
      </div>

      {/* Opens over this dialog; PhotoLightbox handles Escape on the capture
          phase so it closes itself and not the form underneath. */}
      <PhotoLightbox
        visible={lightbox >= 0}
        images={photos.map((p, i) => ({
          url: p.url,
          caption: `${photoCode} — audit photo${photos.length > 1 ? ` ${i + 1} of ${photos.length}` : ''}`,
          sub: 'Found in the audit'
        }))}
        index={lightbox < 0 ? 0 : lightbox}
        onIndex={setLightbox}
        onClose={() => setLightbox(-1)}
      />
    </Modal>
    </FieldCtx.Provider>
  )
}

export default NewAssetDetails
